import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Select } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import classnames from "classnames";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { isNumber } from "utils/fp";

import { useLengthConversion } from "hooks/useUnitConversion";

import { useGroupByStore } from "./GroupByStoreProvider";
import {
  selectLiveSelectedCategory,
  selectSelectedMetadataFilters
} from "./groupBySelectors";
import useGeoRadius from "./hooks/useGeoRadius";

const IGNORE_METADATA_FILTERS_FOR_GROUPS = ["Performance", "Forecast"];

interface DimensionData {
  key: string;
  leftWidth: number;
  rightWidth: number;
  upHeight: number;
  downHeight: number;
}

interface MetadataFilterToolbarProps {
  toolbarActive?: boolean;
  toolbarFocusedIndex?: Maybe<number>;
  onSelectRef?: (index: number, selectEl: Maybe<HTMLElement>) => void;
}

function MetadataFilterToolbar({
  toolbarActive = false,
  toolbarFocusedIndex = null,
  onSelectRef
}: MetadataFilterToolbarProps = {}) {
  const selectedCategory = useGroupByStore(selectLiveSelectedCategory);
  const selectedMetadataFilters = useGroupByStore(selectSelectedMetadataFilters);
  const plays = useSelector((state: RootState) => state.filter.currentPlayList);
  const { data: radiuses } = useGeoRadius();
  const [dimensions, setDimensions] = useState<DimensionData[]>([]);
  const { isReady, convert, getDisplayUnit } = useLengthConversion();

  // Create refs for select components that is an object with blur method
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRefs = useRef<{ [key: string]: Maybe<any> }>({});

  const displayUnit = getDisplayUnit();

  useEffect(() => {
    if (radiuses && radiuses.dimensions && Array.isArray(radiuses.dimensions)) {
      const selectedRadius = selectedMetadataFilters?.["radius"];
      if (!selectedRadius) {
        setDimensions([]);
        return;
      }
      const newDimensions = radiuses.dimensions.find((x) => x.name === selectedRadius);
      if (!newDimensions) {
        setDimensions([]);
        return;
      }

      const convertDimension = (value: number) => {
        if (!isNumber(value)) {
          return 0;
        }
        if (!isReady) {
          return value;
        }
        const converted = convert(value, 2);
        return isNumber(converted) ? converted : value;
      };

      setDimensions([
        {
          key: selectedRadius,
          leftWidth: convertDimension(newDimensions.leftWidth),
          rightWidth: convertDimension(newDimensions.rightWidth),
          upHeight: convertDimension(newDimensions.upHeight),
          downHeight: convertDimension(newDimensions.downHeight)
        }
      ]);
    } else {
      setDimensions([]);
    }
  }, [radiuses, selectedMetadataFilters, isReady]);
  const setMetadataFilter = useGroupByStore((s) => s.setMetadataFilter);

  const changeSelectedMetadataFilter = (key: string, value: string) => {
    setMetadataFilter(key, value);
  };

  // Clear any stale filter values that are no longer valid for the current category
  useEffect(() => {
    selectedCategory?.metadataFilters?.forEach((filter) => {
      const selectedValue = selectedMetadataFilters?.[filter.key];
      if (selectedValue && !filter.options.includes(selectedValue)) {
        changeSelectedMetadataFilter(filter.key, undefined);
      }
    });
  }, [selectedCategory?.name]);

  // width based off of (--field-col-width - --space-4*2) / number of columns => (300-(16*2))/4 = 67
  const columns: ColumnsType<DimensionData> = [
    {
      title: `Left Width (${displayUnit})`,
      dataIndex: "leftWidth",
      key: "leftWidth",
      width: 67
    },
    {
      title: `Right Width (${displayUnit})`,
      dataIndex: "rightWidth",
      key: "rightWidth",
      width: 67
    },
    {
      title: `Up Height (${displayUnit})`,
      dataIndex: "upHeight",
      key: "upHeight",
      width: 67
    },
    {
      title: `Down Height (${displayUnit})`,
      dataIndex: "downHeight",
      key: "downHeight",
      width: 67
    }
  ];

  if (
    selectedCategory?.metadataFilters?.length === 0 ||
    IGNORE_METADATA_FILTERS_FOR_GROUPS.includes(selectedCategory?.name)
  ) {
    return null;
  }
  return (
    <Wrapper>
      {selectedCategory?.metadataFilters?.map((filter, index) => {
        const selectedValue = selectedMetadataFilters[filter.key] || undefined;

        // Calculate if this specific Select should be focused
        const isFocused = toolbarActive && toolbarFocusedIndex === index;

        return (
          <SelectWrapper key={filter.key}>
            <label>{filter.name}</label>
            <Select
              ref={(el) => {
                selectRefs.current[filter.key] = el;
                if (onSelectRef) onSelectRef(index, el as HTMLElement);
              }}
              className={classnames("toolbar-item", {
                "keyboard-focused": isFocused
              })}
              data-testid={`metadata-select-${filter.key}`}
              showSearch={true}
              allowClear={true}
              value={selectedValue}
              onChange={(value) => {
                changeSelectedMetadataFilter(filter.key, value);
                // Blur the select after selection
                if (selectRefs.current[filter.key]) {
                  selectRefs.current[filter.key].blur();
                }
              }}
              options={filter.options.map((option) => ({
                label: option,
                value: option
              }))}
              popupClassName="modal-select"
              placeholder="All"
            />
          </SelectWrapper>
        );
      })}
      {selectedCategory?.metadataFilters?.find((x) => x.key === "radius") &&
        plays?.length === 1 &&
        Array.isArray(dimensions) &&
        dimensions.length > 0 &&
        displayUnit && (
          <TableWrapperRow>
            <TableWrapper>
              <Table
                data-testid="geo-calc-table"
                columns={columns}
                dataSource={dimensions}
                rowKey="key"
                pagination={false}
                size="small"
                bordered={true}
              />
            </TableWrapper>
          </TableWrapperRow>
        )}
    </Wrapper>
  );
}

export default MetadataFilterToolbar;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 0 var(--space-4);
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SelectWrapper = styled.div`
  min-width: 100px;
  width: 100%;
  .ant-select {
    width: 100%;
  }
`;

const TableWrapperRow = styled.div`
  display: flex;
  width: 100%;
`;
